import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BentoGrid, BentoCard } from "@/components/ui/bento-grid";
import { Sparkles, MessageSquare, Store, CreditCard, Settings, Rocket } from "lucide-react";
import { Check } from "lucide-react";
import { SiteLayout } from "@/layouts/site-layout";
import { SEO } from "@/components/SEO";

const Packages = () => {
  const mainPackages = [
    {
      title: "One Week MVP Build",
      tagline: "Our flagship package — ship in 7 days.",
      price: "$1,999",
      timeline: "7 days",
      seats: "4 per week",
      features: [
        "Full design & development",
        "Auth, payments, admin dashboard",
        "AI integrations",
        "Production deployment",
        "30 days support"
      ]
    },
    {
      title: "Custom MVP Estimate",
      tagline: "For complex builds that need more time.",
      price: "Quoted after scoping call",
      timeline: "Flexible (typically 2–4 weeks)",
      seats: "Custom schedule",
      features: [
        "Enterprise-level products",
        "Multi-platform integrations",
        "Advanced AI implementations",
        "Products with regulatory requirements"
      ]
    }
  ];

  const addOns = [
    { emoji: "🎥", title: "Launch Video Package", description: "Professional promo video for Product Hunt and social channels.", price: "From $500" },
    { emoji: "🚀", title: "Product Hunt Launch Kit", description: "Complete launch setup: design, copy, listings on 20+ directories.", price: "From $750" },
    { emoji: "🛠", title: "Lead-Gen Micro-Tool", description: "Custom free tool (calculator, quiz, etc.) to drive signups.", price: "From $1,500" },
    { emoji: "🔧", title: "Post-Launch Support", description: "Monthly iteration and support packages.", price: "From $1,200/month" }
  ];

  const comparisonData = [
    { feature: "Delivery Time", oneWeek: "7 days 🚀", custom: "Flexible", addOns: "Varies" },
    { feature: "Price Range", oneWeek: "$1,999 (50% OFF: $999)", custom: "Quoted", addOns: "From $500" },
    { feature: "Best For", oneWeek: "Fast launchers", custom: "Complex products", addOns: "Launch amplification" },
    { feature: "Features", oneWeek: "Core MVP (auth, payments, AI, dashboard)", custom: "Custom scope", addOns: "Marketing & support tools" },
    { feature: "Seats Available", oneWeek: "4/week", custom: "Custom schedule", addOns: "Anytime" }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "name": "MVP Packages",
    "description": "Choose from our one week MVP build package or custom MVP development options",
    "itemListElement": [
      {
        "@type": "Offer",
        "name": "One Week MVP Build",
        "price": "1999",
        "priceCurrency": "USD",
        "description": "Full design & development MVP delivered in 7 days"
      },
      {
        "@type": "Offer",
        "name": "Custom MVP Estimate",
        "description": "Flexible timeline for complex MVP builds"
      }
    ]
  };

  return (
    <SiteLayout>
      <SEO
        title="MVP Packages & Pricing | Flat-Fee MVP Development Packages"
        description="Explore our MVP packages: One Week MVP Build ($1,999 - Limited time 50% off: $999) and Custom MVP options. Flat-fee MVP development package, agency MVP package, and founder MVP package with rapid turnaround."
        keywords="MVP packages, MVP pricing, flat-fee MVP development package, agency MVP package, founder MVP package, one week MVP package for startup founders, agency partnership MVP package with rapid turnaround, full MVP build package including design and development for startups"
        canonicalUrl={`${typeof window !== 'undefined' ? window.location.origin : 'https://oneweekmvps.com'}/packages`}
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-heading leading-tight">
              Pick your <span className="text-primary">build package</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground">
              Fast MVPs. Custom builds. Launch extras. Choose what fits your timeline and ambition.
            </p>
          </div>
        </div>
      </section>

      {/* Main Package Cards */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {mainPackages.map((pkg, idx) => (
              <Card key={idx} className="border-border/50 relative overflow-hidden">
                {idx === 0 && (
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded text-sm font-medium">
                    Most Popular
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-3xl">{pkg.title}</CardTitle>
                  <CardDescription className="text-base">{pkg.tagline}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    {idx === 0 ? (
                      <div className="space-y-1 mb-2">
                        <div className="text-sm text-muted-foreground line-through">$1,999</div>
                        <div className="text-4xl font-heading">$999</div>
                        <div className="text-sm text-primary font-medium">50% OFF Limited Time</div>
                      </div>
                    ) : (
                      <div className="text-4xl font-heading mb-2">{pkg.price}</div>
                    )}
                    <div className="text-sm text-muted-foreground space-y-1 mt-3">
                      <div><strong>Timeline:</strong> {pkg.timeline}</div>
                      <div><strong>Seats:</strong> {pkg.seats}</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3">{idx === 0 ? "What's Included:" : "Best For:"}</h4>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, featureIdx) => (
                        <li key={featureIdx} className="flex gap-2">
                          <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button className="w-full" size="lg" asChild>
                    {idx === 0 ? (
                      <a href="https://checkout.dodopayments.com/buy/pdt_EGEdTJ9NOLaMWTDmwqhbv?session=sess_HIaYqixlwI">Reserve Your Seat</a>
                    ) : (
                      <a href="https://cal.com/buildwithrishi/15min">Calculate My Estimate</a>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What You Can Build - Bento Grid */}
      <section className="py-24 md:py-32">
        <div className="section-container">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-display font-medium leading-tight">
                What you can <span className="text-primary">build with us</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Real MVPs launched in one week across different industries
              </p>
            </div>

            <BentoGrid className="lg:grid-rows-3">
              {[
                {
                  Icon: Sparkles,
                  name: "AI-Powered Tools",
                  description: "Dashboards, analytics platforms, and AI-powered automation tools.",
                  href: "/packages",
                  cta: "See Pricing",
                  background: <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />,
                  className: "lg:col-start-1 lg:col-end-2 lg:row-start-1 lg:row-end-3",
                },
                {
                  Icon: MessageSquare,
                  name: "Chatbots & Assistants",
                  description: "Customer support bots, onboarding assistants, and knowledge bases.",
                  href: "/packages",
                  cta: "Learn More",
                  background: <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />,
                  className: "lg:col-start-2 lg:col-end-3 lg:row-start-1 lg:row-end-2",
                },
                {
                  Icon: Store,
                  name: "Marketplaces",
                  description: "Two-sided platforms connecting buyers and sellers seamlessly.",
                  href: "/packages",
                  cta: "Get Started",
                  background: <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />,
                  className: "lg:col-start-2 lg:col-end-3 lg:row-start-2 lg:row-end-3",
                },
                {
                  Icon: CreditCard,
                  name: "Subscription Apps",
                  description: "SaaS platforms with auth, billing, and admin dashboards ready to scale.",
                  href: "/packages",
                  cta: "View Plans",
                  background: <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />,
                  className: "lg:col-start-3 lg:col-end-4 lg:row-start-1 lg:row-end-3",
                },
                {
                  Icon: Settings,
                  name: "Internal Tools",
                  description: "Custom admin panels, dashboards, and workflow automation systems.",
                  href: "/packages",
                  cta: "Explore",
                  background: <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />,
                  className: "lg:col-start-1 lg:col-end-2 lg:row-start-3 lg:row-end-4",
                },
                {
                  Icon: Rocket,
                  name: "Launch Pages",
                  description: "Product Hunt-ready pages, waitlist funnels, and lead-gen tools.",
                  href: "/packages",
                  cta: "Launch Now",
                  background: <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-primary/10 blur-3xl" />,
                  className: "lg:col-start-2 lg:col-end-4 lg:row-start-3 lg:row-end-4",
                },
              ].map((feature) => (
                <BentoCard key={feature.name} {...feature} />
              ))}
            </BentoGrid>
          </div>
        </div>
      </section>

      {/* Add-On Packages */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-6xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                Launch extras to <span className="text-primary">amplify your MVP</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {addOns.map((addon, idx) => (
                <Card key={idx} className="border-border/50 text-center">
                  <CardHeader>
                    <div className="text-4xl mb-2">{addon.emoji}</div>
                    <CardTitle className="text-lg">{addon.title}</CardTitle>
                    <CardDescription className="text-sm">{addon.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-xl font-heading text-primary mb-4">{addon.price}</div>
                    <Button variant="outline" size="sm" className="w-full">Add to My Build</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Chart */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-5xl mx-auto space-y-12">
            <div className="text-center space-y-4">
              <h2 className="text-4xl md:text-5xl font-heading">
                Compare your <span className="text-primary">options</span>
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-4 px-4"></th>
                    <th className="text-left py-4 px-4 font-heading text-lg">One Week MVP</th>
                    <th className="text-left py-4 px-4 font-heading text-lg">Custom MVP</th>
                    <th className="text-left py-4 px-4 font-heading text-lg">Add-Ons</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, idx) => (
                    <tr key={idx} className="border-b border-border/50">
                      <td className="py-4 px-4 font-medium">{row.feature}</td>
                      <td className="py-4 px-4 text-muted-foreground">{row.oneWeek}</td>
                      <td className="py-4 px-4 text-muted-foreground">{row.custom}</td>
                      <td className="py-4 px-4 text-muted-foreground">{row.addOns}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-card/30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading">
              Don't let your idea wait <span className="text-primary">another week</span>
            </h2>
            <p className="text-xl text-muted-foreground">
              Seats are limited. Quality is not.
            </p>
            <p className="text-lg text-foreground/90">
              Book your build week now and launch by Sunday.
            </p>
            <Button size="lg" className="text-base px-8" asChild>
              <a href="https://checkout.dodopayments.com/buy/pdt_EGEdTJ9NOLaMWTDmwqhbv?session=sess_HIaYqixlwI">Reserve My Seat</a>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default Packages;
